/**
 * Web entitlement bridge.
 *
 * The Expo app backs this with SecureStore; the web build has localStorage and
 * nothing better, which is honest: a browser cannot keep a secret from its own
 * user. The activation token is signed and device-bound either way, so the
 * worst a tampered store buys is a reset trial — and clearing site data also
 * clears the user's own clients, products and history.
 */

import type { KVStore } from '../shared/entitlements';
import { API_BASE } from '../shared/config';
import { normalizeLicenseKey } from '../shared/license';
import { clearActivation, storeActivation } from '../shared/entitlements';

export const webStore: KVStore = {
  async get(key) {
    try { return localStorage.getItem(key); } catch { return null; }
  },
  async set(key, value) {
    try { localStorage.setItem(key, value); } catch { /* private mode */ }
  },
  async remove(key) {
    try { localStorage.removeItem(key); } catch { /* private mode */ }
  },
};

const DEVICE_KEY = 'qdp.webDeviceId';

/**
 * Browser device id.
 *
 * A random value in localStorage, not a fingerprint: fingerprinting users of a
 * privacy-first product to enforce its licence would contradict the pitch. It
 * means clearing site data yields a new device slot, which is why the licence
 * allows three and why /api/deactivate exists.
 */
export async function getWebDeviceId(): Promise<string> {
  let id = await webStore.get(DEVICE_KEY);
  if (!id) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    id = 'W' + Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('').toUpperCase();
    await webStore.set(DEVICE_KEY, id);
  }
  return id;
}

export type ActivationOutcome =
  | { ok: true }
  | { ok: false; reason: 'invalid-key' | 'device-limit' | 'offline' };

export async function activateLicense(rawKey: string): Promise<ActivationOutcome> {
  const key = normalizeLicenseKey(rawKey);
  const deviceId = await getWebDeviceId();

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/activate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key, deviceId }),
    });
  } catch {
    return { ok: false, reason: 'offline' };
  }

  if (res.status === 409) return { ok: false, reason: 'device-limit' };
  if (!res.ok) return { ok: false, reason: 'invalid-key' };

  const payload = await res.json().catch(() => null);
  if (!payload?.token) return { ok: false, reason: 'offline' };

  await webStore.set('qdp.licenseKey', key);
  await storeActivation(webStore, payload.token, payload.ref ?? '');
  return { ok: true };
}

export async function deactivateLicense(): Promise<void> {
  const key = await webStore.get('qdp.licenseKey');
  if (key) {
    try {
      await fetch(`${API_BASE}/deactivate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, deviceId: await getWebDeviceId() }),
      });
    } catch {
      // Honour the user's intent locally regardless.
    }
  }
  await webStore.remove('qdp.licenseKey');
  await clearActivation(webStore);
}
